import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { useBeta } from '../../../contexts/BetaContext';
import './WorkItemWelcomeModal.scss';

/**
 * 新版工作項目「歡迎導覽」Modal(取代舊 driver.js tour)。
 * 一頁一個「示範畫面」(假資料 + 沿用 wi-* 樣式)+ 說明,可上下頁。
 * 不依賴使用者真實資料、不碰 DB(觸發由外層用 wi_tour_completed 控制)。
 *
 * 示範畫面用 <div className="workitem-app wi-demo"> 包起來,讓 wi-* 樣式生效;
 * wi-demo 在 scss 裡把 .workitem-app 的 height/背景收斂成「展示卡」尺寸。
 */

// ── 假資料 ──
const DEMO_STATUS = { open: '#4f46e5', in_progress: '#0e9384', verify: '#f79009', rd_fixed: '#17b26a' };
const pill = (label, color) => (
  <span className="wi-status-pill" style={{ background: color, color: '#fff' }}>{label}</span>
);
const typeTag = (label, bg, fg) => <span className="wi-type" style={{ background: bg, color: fg }}>{label}</span>;
const prioTag = (label, color) => <span className="wi-prio" style={{ background: color, color: '#fff' }}>⚑ {label}</span>;
const ava = (bg, txt) => <span className="wi-ava" style={{ background: bg }}>{txt}</span>;

// ── 6 頁 ──
const PAGES = [
  {
    key: 'welcome',
    title: '歡迎使用全新的工作項目',
    desc: '我們把 Ticket 和 Phase 合併成單一「工作項目」,更直覺地管理團隊的每件事。花一分鐘帶你看過一輪。',
    stage: () => (
      <div className="wi-demo-hero">
        <div className="wi-demo-hero-icon">🗂️</div>
        <div className="wi-demo-hero-title">工作項目</div>
        <div className="wi-demo-hero-sub">群組 · 子項 · 交付紀錄 · 跨專案查詢</div>
      </div>
    ),
  },
  {
    key: 'list',
    title: '列表視圖:一列看懂一件工作',
    desc: '每個工作項目一列,直接顯示 狀態、指派人、最後更新、優先度、類型。用群組分類(取代舊的 Phase),上方篩選列可依狀態 / 優先 / 指派 / 類型 / 群組快速過濾。',
    stage: () => (
      <div className="workitem-app wi-demo">
        <div className="wi-body">
          <div className="wi-list">
            <div className="wi-group" style={{ '--wi-accent': '#4f46e5' }}>
              <div className="wi-group-header">
                <span className="wi-group-name">前台改版</span>
                <span className="wi-group-count">2 項</span>
              </div>
              <div className="wi-row">
                <span className="wi-no">#12</span>
                <span className="wi-col wi-col-status">{pill('進行中', DEMO_STATUS.in_progress)}</span>
                <span className="wi-title">登入頁 RWD 調整</span>
                <span className="wi-col wi-col-assignee">{ava('#6938ef', '陳')}</span>
                <span className="wi-col wi-col-updated">3 小時前</span>
                <span className="wi-col wi-col-prio">{prioTag('高', '#d92d20')}</span>
                <span className="wi-col wi-col-type">{typeTag('DesignChange', '#dbeafe', '#2563eb')}</span>
              </div>
              <div className="wi-row">
                <span className="wi-no">#13</span>
                <span className="wi-col wi-col-status">{pill('待驗收', DEMO_STATUS.verify)}</span>
                <span className="wi-title">首頁 Banner 輪播 bug</span>
                <span className="wi-col wi-col-assignee">{ava('#0e7090', '林')}</span>
                <span className="wi-col wi-col-updated">昨天</span>
                <span className="wi-col wi-col-prio">{prioTag('中', '#f79009')}</span>
                <span className="wi-col wi-col-type">{typeTag('Issue', '#fee4e2', '#d92d20')}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    ),
  },
  {
    key: 'create',
    title: '新增工作項目',
    desc: '點右上角「新增工作項目」,可指定 專案 / 群組 / 類型 / Owner / 協作者,並用 Markdown 寫說明。建立後可在詳情裡再拆成子項。',
    stage: () => (
      <div className="wi-demo-form">
        <div className="wi-demo-form-row"><span className="wi-demo-label">專案</span><span className="wi-demo-input">前台官網</span></div>
        <div className="wi-demo-form-row"><span className="wi-demo-label">標題</span><span className="wi-demo-input">結帳流程優化</span></div>
        <div className="wi-demo-form-row"><span className="wi-demo-label">Owner</span><span className="wi-demo-input">{ava('#dd2590', '王')} 王小明</span></div>
        <div className="wi-demo-form-row2">
          <div className="wi-demo-form-row"><span className="wi-demo-label">類型</span><span>{typeTag('Task', '#f2f4f7', '#475467')}</span></div>
          <div className="wi-demo-form-row"><span className="wi-demo-label">優先</span><span>{prioTag('高', '#d92d20')}</span></div>
        </div>
      </div>
    ),
  },
  {
    key: 'detail',
    title: '點開工作項目 → 詳情',
    desc: '點任一列會從右側展開詳情:可拆解子項(function list)、留言討論、上傳附件,還能記錄每次「交付紀錄」(版本 / 環境 / 附件),交接一目了然。',
    stage: () => (
      <div className="wi-demo-drawer">
        <div className="wi-demo-drawer-head"><span className="wi-no">#12</span> 登入頁 RWD 調整 {pill('進行中', DEMO_STATUS.in_progress)}</div>
        <div className="wi-demo-sec">
          <div className="wi-demo-sec-title">子項</div>
          <div className="wi-demo-sub">✓ 手機版排版</div>
          <div className="wi-demo-sub">○ 平板斷點</div>
        </div>
        <div className="wi-demo-sec">
          <div className="wi-demo-sec-title">交付紀錄</div>
          <div className="wi-demo-delivery">v1.2.0 · staging · 附 2 個檔</div>
        </div>
        <div className="wi-demo-sec">
          <div className="wi-demo-sec-title">留言</div>
          <div className="wi-demo-comment">{ava('#039855', '陳')} 手機版已修好,請驗收</div>
        </div>
      </div>
    ),
  },
  {
    key: 'mine',
    title: '我的工作項目:跨專案總覽',
    desc: '左側「我的工作項目」把所有專案裡指派給你的事情收在一起,母項 + 子項縮排呈現,不用一個個專案點進去找。',
    stage: () => (
      <div className="workitem-app wi-demo">
        <div className="wi-body">
          <div className="wi-list">
            <div className="wi-proj-section">
              <div className="wi-proj-head"><span className="wi-proj-dot" style={{ background: '#4f46e5' }} /><span className="wi-proj-name">前台官網</span></div>
              <div className="wi-row"><span className="wi-no">#12</span><span className="wi-col wi-col-status">{pill('進行中', DEMO_STATUS.in_progress)}</span><span className="wi-title">登入頁 RWD 調整</span></div>
            </div>
            <div className="wi-proj-section">
              <div className="wi-proj-head"><span className="wi-proj-dot" style={{ background: '#17b26a' }} /><span className="wi-proj-name">商用充電</span></div>
              <div className="wi-row"><span className="wi-no">#5</span><span className="wi-col wi-col-status">{pill('待辦', DEMO_STATUS.open)}</span><span className="wi-title">後台報表匯出</span></div>
            </div>
          </div>
        </div>
      </div>
    ),
  },
  {
    key: 'search',
    title: '全系統工作項目查詢',
    desc: '需要跨專案找東西?「全系統查詢」用關鍵字 + 狀態 / 優先 / 指派 / 類型篩選,一張平表看遍全公司的工作項目。',
    stage: () => (
      <div className="workitem-app wi-demo">
        <div className="wi-body">
          <div className="wi-ft-wrap">
            <table className="wi-flat-table">
              <thead><tr><th>#</th><th>狀態</th><th>標題</th><th>專案</th><th>最後更新</th><th>類型</th></tr></thead>
              <tbody>
                <tr className="wi-ft-row"><td>#12</td><td>{pill('進行中', DEMO_STATUS.in_progress)}</td><td className="wi-ft-title">登入頁 RWD 調整</td><td><span className="wi-ft-proj"><span className="wi-ft-proj-dot" style={{ background: '#4f46e5' }} />前台官網</span></td><td>3 小時前</td><td>{typeTag('DesignChange', '#dbeafe', '#2563eb')}</td></tr>
                <tr className="wi-ft-row"><td>#5</td><td>{pill('待辦', DEMO_STATUS.open)}</td><td className="wi-ft-title">後台報表匯出</td><td><span className="wi-ft-proj"><span className="wi-ft-proj-dot" style={{ background: '#17b26a' }} />商用充電</span></td><td>昨天</td><td>{typeTag('Task', '#f2f4f7', '#475467')}</td></tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    ),
  },
];

const WorkItemWelcomeModal = ({ open, onClose }) => {
  const { updateBetaSetting } = useBeta();
  const [page, setPage] = useState(0);
  if (!open) return null;

  const isFirst = page === 0;
  const isLast = page === PAGES.length - 1;
  const cur = PAGES[page];
  // 看完/略過 → 記錄新 key(不碰舊 beta_tour_completed);replay 重寫 true 冪等無害
  const finish = () => { updateBetaSetting('wi_tour_completed', true); setPage(0); onClose?.(); };

  return createPortal(
    <div className="wi-welcome-overlay" onMouseDown={(e) => { if (e.target === e.currentTarget) finish(); }}>
      <div className="wi-welcome">
        <button className="wi-welcome-skip" onClick={finish}>略過</button>

        <div className="wi-welcome-stage">{cur.stage()}</div>

        <div className="wi-welcome-body">
          <h2 className="wi-welcome-title">{cur.title}</h2>
          <p className="wi-welcome-desc">{cur.desc}</p>
        </div>

        <div className="wi-welcome-foot">
          <div className="wi-welcome-dots">
            {PAGES.map((p, i) => (
              <button
                key={p.key}
                className={`wi-welcome-dot ${i === page ? 'active' : ''}`}
                onClick={() => setPage(i)}
                aria-label={`第 ${i + 1} 頁`}
              />
            ))}
          </div>
          <div className="wi-welcome-actions">
            {!isFirst && <button className="wi-btn-secondary" onClick={() => setPage((p) => p - 1)}>上一步</button>}
            {!isLast
              ? <button className="wi-btn-primary" onClick={() => setPage((p) => p + 1)}>下一步</button>
              : <button className="wi-btn-primary" onClick={finish}>開始使用</button>}
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default WorkItemWelcomeModal;
